import { useState } from 'react';

/**
 * CopyButton — queue #644 §UAT-2026-07-07-R644 §R644-4.
 *
 * common-showcase/components/TakeThisPanel.tsx 의 모듈-로컬 비-export `CopyButton` 헬퍼를
 * 공유 위치(ui-kit)로 승격한 컴포넌트. `navigator.clipboard.writeText` + 1.5초 "복사됨" 상태
 * 토글만 담당 — ui-kit 은 다른 컴포넌트(AppShellLabels 등)와 동일하게 i18n 비의존이므로 라벨은
 * 호출부가 이미 번역된 문자열로 전달한다(react-i18next 를 여기서 직접 호출하지 않는다).
 */
export interface CopyButtonProps {
  /** 클립보드에 복사할 텍스트 */
  text: string;
  /** 버튼의 aria-label (대상 블록을 명시) */
  ariaLabel: string;
  /** 기본 상태 라벨(예: "복사") */
  copyLabel: string;
  /** 복사 완료 후 표시할 라벨(예: "복사됨") */
  copiedLabel: string;
  /**
   * queue #653 §R653-3: 클립보드 결과와 무관하게 "복사 시도" 자체를 호출부에 알리는 훅.
   * TakeThisPanel 의 DOMAIN_ROOT 미입력 경고(필드 강조)처럼, 복사 클릭을 트리거로 삼는
   * 부가 로직을 위해 옵션으로 추가 — 기존 호출부는 전달하지 않으면 동작 무변화.
   */
  onCopy?: () => void;
}

export function CopyButton({ text, ariaLabel, copyLabel, copiedLabel, onCopy }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    onCopy?.();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard 권한 부재 등 — 무음 실패, "복사됨" 피드백만 미노출
    }
  }

  return (
    <button
      type="button"
      onClick={() => { void handleCopy(); }}
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        background: 'none',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: '2px var(--spacing-2)',
        fontSize: 'var(--font-size-xs)',
        color: copied ? 'var(--color-success, #16a34a)' : 'var(--color-brand-primary)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family-sans)',
      }}
    >
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}
